import { useEffect, useState } from "react";
import api, { gbp } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, SlidersHorizontal } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" }) : "—");

export default function AdjustmentReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/reports/adjustments")
      .then((r) => setRows(r.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, []);

  const total = rows.length;
  const clients = new Set(rows.map((r) => r.client_name).filter(Boolean)).size;

  return (
    <div data-testid="adjustment-report">
      <PageHeader title="Adjustment Approvals Report" subtitle="Every manual fee adjustment made on proposals, with the approver, reason and movement." />
      <div className="p-8 space-y-5">
        <div className="flex gap-3 flex-wrap">
          <Card className="p-4 rounded-md min-w-[140px]">
            <div className="text-2xl font-bold tabular text-cass-navy" data-testid="adj-count">{total}</div>
            <div className="text-xs text-cass-slate mt-0.5">Adjustments</div>
          </Card>
          <Card className="p-4 rounded-md min-w-[140px]">
            <div className="text-2xl font-bold tabular text-cass-navy">{clients}</div>
            <div className="text-xs text-cass-slate mt-0.5">Clients affected</div>
          </Card>
        </div>

        <Card className="rounded-md overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-cass-slate">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-cass-slate" data-testid="adj-empty">
              <SlidersHorizontal className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No manual fee adjustments recorded yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-cass-slate/70 border-b border-border">
                    <th className="px-4 py-3 font-semibold">Proposal</th>
                    <th className="px-4 py-3 font-semibold">Client</th>
                    <th className="px-4 py-3 font-semibold">Service</th>
                    <th className="px-4 py-3 font-semibold">Movement</th>
                    <th className="px-4 py-3 font-semibold">Reason</th>
                    <th className="px-4 py-3 font-semibold">By</th>
                    <th className="px-4 py-3 font-semibold">When</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((a, i) => {
                    const from = a.from ?? a.amount;
                    const to = a.to ?? a.amount;
                    return (
                      <tr key={i} className="border-b border-border/60 last:border-0 hover:bg-cass-cloud/40" data-testid={`adj-row-${i}`}>
                        <td className="px-4 py-3 font-medium text-cass-navy whitespace-nowrap">
                          {a.reference || "—"}
                          {a.status && <Badge variant="outline" className="ml-2 capitalize">{a.status}</Badge>}
                        </td>
                        <td className="px-4 py-3 text-cass-navy">{a.client_name || "—"}</td>
                        <td className="px-4 py-3 text-cass-slate">{a.service || a.service_id || "—"}</td>
                        <td className="px-4 py-3 whitespace-nowrap tabular">
                          <span className="text-cass-slate">{gbp(from)}</span>
                          <ArrowRight className="w-3 h-3 inline mx-1 text-cass-slate/60" />
                          <span className="font-semibold text-cass-navy">{gbp(to)}</span>
                          {a.recommended != null && <span className="text-xs text-cass-slate/70 ml-1">(rec {gbp(a.recommended)})</span>}
                        </td>
                        <td className="px-4 py-3 text-cass-slate max-w-[240px]">{a.reason || "—"}</td>
                        <td className="px-4 py-3 text-cass-slate whitespace-nowrap">{a.by || "—"}</td>
                        <td className="px-4 py-3 text-cass-slate whitespace-nowrap">{fmtDate(a.at)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
